import Cookies from "js-cookie";
import { logCookie, logCompatibility } from "./session-logger";

// Cache for domain detection to avoid repeated calculations
let cachedDomain = null;
let cachedHostname = null;

const isDomainOrSubdomain = (hostname, root) =>
  hostname === root || hostname.endsWith(`.${root}`);

export const resolveCookieDomain = (hostname) => {
  if (isDomainOrSubdomain(hostname, 'exacc.au')) return '.exacc.au';
  if (isDomainOrSubdomain(hostname, 'ssx.au')) return '.ssx.au';
  if (isDomainOrSubdomain(hostname, 'jimssoft.com')) return '.jimssoft.com';
  if (isDomainOrSubdomain(hostname, 'ipasoft.au')) return '.ipasoft.au';
  if (isDomainOrSubdomain(hostname, 'exx.au')) return '.exx.au';
  if (isDomainOrSubdomain(hostname, 'olivs.app')) {
    const labels = hostname.split('.');
    return `.${labels.length > 2 ? labels.slice(1).join('.') : hostname}`;
  }
  return hostname;
};

/**
 * Determine the appropriate domain for cross-domain cookies
 * @returns {string} - The domain to use for cookies (e.g., '.exacc.au')
 */
export const getDomainForCookies = () => {
  const hostname = window.location.hostname;
  
  // Return cached result if hostname hasn't changed
  if (cachedHostname === hostname && cachedDomain) {
    return cachedDomain;
  }
  
  // Extract base domain for cross-subdomain cookies
  const domain = resolveCookieDomain(hostname);
  
  // Cache the result
  cachedHostname = hostname;
  cachedDomain = domain;
  
  return domain;
};

/**
 * Set a cross-domain cookie that works across all subdomains
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Additional cookie options
 */
export const setCrossDomainCookie = (name, value, options = {}) => {
  try {
    const domain = getDomainForCookies();
    const isProduction = domain.startsWith('.');
    const isHttps = window.location.protocol === 'https:';
    
    // Log HTTPS requirement for secure cookies
    logCompatibility.httpsRequired(isHttps, { domain, isProduction });
    
    const cookieOptions = {
      path: '/',
      domain: isProduction ? domain : undefined, // Only set domain for production
      secure: isHttps, // Secure flag for HTTPS
      sameSite: 'Lax',
      expires: 7, // 7 days default
      ...options
    };
    
    Cookies.set(name, value, cookieOptions);
    logCookie.set(name, true, { domain, isProduction, isHttps });
  } catch (error) {
    logCookie.set(name, false, { error: error.message, domain: getDomainForCookies() });
    // Fallback to basic cookie setting
    try {
      Cookies.set(name, value, { path: '/', ...options });
      logCookie.set(name, true, { fallback: true });
    } catch (fallbackError) {
      logCookie.set(name, false, { error: fallbackError.message, fallback: true });
    }
  }
};

/**
 * Get cookie value (enhanced version with better parsing)
 * @param {string} name - Cookie name
 * @returns {string|undefined} - Cookie value or undefined if not found
 */
export const getCookie = (name) => {
  try {
    // First try js-cookie method
    const value = Cookies.get(name);
    if (value !== undefined) {
      logCookie.get(name, true, { method: 'js-cookie' });
      return value;
    }
    
    // Fallback to manual parsing for edge cases
    const value2 = `; ${document.cookie}`;
    const parts = value2.split(`; ${name}=`);
    if (parts.length === 2) {
      const result = parts.pop().split(";").shift();
      logCookie.get(name, true, { method: 'manual-parse' });
      return result;
    }
    
    logCookie.get(name, false);
    return undefined;
  } catch (error) {
    logCookie.get(name, false, { error: error.message });
    return undefined;
  }
};

export const SESSION_COOKIE_NAMES = [
  'sessionId',
  'apiKey',
  'accessKey',
  'refreshKey',
  'authxSec2YN',
  'apiKeyExpirationUTCDate',
  'globalUserId',
  'xxuStatus',
  'emailVerified',
  'phoneVerified',
  'loginEmail',
  'firstName',
  'lastName',
  'loginSecuredHash',
  'loginLastPwdUTCDate',
  'login2FaCodeLastUsedUTCDate',
  'loginLastSec2CheckInUTCDate',
  'databaseId',
  'sessionGuid',
  'login2FASecKey',
  'theme',
  'loginPassword',
];

/**
 * Clear all session-related cookies
 */
export const clearAllCookies = () => {
  const domain = getDomainForCookies();
  const isProduction = domain.startsWith('.');
  let clearedCount = 0;
  
  SESSION_COOKIE_NAMES.forEach(cookieName => {
    try {
      // Clear with domain (for cross-domain cookies)
      if (isProduction) {
        Cookies.remove(cookieName, { path: '/', domain });
      }
      
      // Clear without domain (for local cookies)
      Cookies.remove(cookieName, { path: '/' });
      
      // Clear with current hostname (additional cleanup)
      Cookies.remove(cookieName, { path: '/', domain: window.location.hostname });
      
      clearedCount++;
    } catch (error) {
      logCookie.domainError(domain, error, { cookieName });
    }
  });
  
  logCookie.clear(clearedCount, { domain, isProduction });
};

/**
 * Clear session storage
 */
export const clearSessionStorage = () => {
  try {
    sessionStorage.clear();
    logCompatibility.localStorageSupport(true);
  } catch (error) {
    logCompatibility.localStorageSupport(false, { error: error.message });
  }
};

/**
 * Get all session cookies as an object
 * @returns {Object} - Object containing all session cookie values
 */
export const getAllSessionCookies = () => {
  const sessionCookies = [
    'sessionId',
    'apiKey',
    'accessKey',
    'refreshKey',
    'authxSec2YN',
    'apiKeyExpirationUTCDate',
    'globalUserId',
    'loginEmail',
    'firstName',
    'lastName',
    'loginSecuredHash',
    'loginLastPwdUTCDate',
    'login2FaCodeLastUsedUTCDate',
    'loginLastSec2CheckInUTCDate',
    'databaseId',
    'theme'
  ];
  
  const cookies = {};
  sessionCookies.forEach(name => {
    const value = getCookie(name);
    if (value && value !== 'undefined') {
      cookies[name] = value;
    }
  });
  
  return cookies;
};

/**
 * Set multiple session cookies at once (optimized batch operation)
 * @param {Object} cookieData - Object with cookie name-value pairs
 * @param {Object} options - Cookie options to apply to all cookies
 */
export const setSessionCookies = (cookieData, options = {}) => {
  const domain = getDomainForCookies();
  const isProduction = domain.startsWith('.');
  const isHttps = window.location.protocol === 'https:';
  
  // Pre-calculate cookie options once for all cookies
  const cookieOptions = {
    path: '/',
    domain: isProduction ? domain : undefined,
    secure: isHttps,
    sameSite: 'Lax',
    expires: 7,
    ...options
  };
  
  // Batch set cookies with pre-calculated options
  Object.entries(cookieData).forEach(([name, value]) => {
    if (value && value !== 'undefined') {
      try {
        Cookies.set(name, value, cookieOptions);
        logCookie.set(name, true, { batch: true, domain, isProduction, isHttps });
      } catch (error) {
        logCookie.set(name, false, { error: error.message, batch: true });
        // Fallback for individual cookie
        try {
          Cookies.set(name, value, { path: '/', ...options });
          logCookie.set(name, true, { fallback: true, batch: true });
        } catch (fallbackError) {
          logCookie.set(name, false, { error: fallbackError.message, fallback: true, batch: true });
        }
      }
    }
  });
};
